import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { bladesApi } from "../../api/blades";

function NodeCard({ node, isCurrent }) {
  const inner = (
    <div className={`rounded-lg border px-4 py-3 min-w-[160px] max-w-[220px] text-center transition-colors ${
      isCurrent
        ? "border-bh-500 bg-bh-50 ring-1 ring-bh-400 cursor-default"
        : "border-gray-200 bg-white hover:border-bh-300 hover:bg-bh-50"
    }`}>
      {node.hyco_id && (
        <p className="text-xs font-mono text-bh-600 mb-0.5">{node.hyco_id}</p>
      )}
      <p className={`text-sm font-semibold truncate ${isCurrent ? "text-bh-800" : "text-gray-800"}`}>
        {node.name}
      </p>
      <p className="text-xs text-gray-400 mt-0.5 truncate">{node.project_name}</p>
      {node.is_released && !isCurrent && (
        <span className="inline-block mt-1 text-[10px] font-medium text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-full px-1.5 py-0.5">
          Released
        </span>
      )}
    </div>
  );

  if (isCurrent) return inner;
  return (
    <Link to={`/projects/${node.project_id}/blade/${node.id}/summary`}>
      {inner}
    </Link>
  );
}

function Connector({ branches = 1 }) {
  if (branches <= 1) {
    return (
      <div className="flex flex-col items-center my-1">
        <div className="w-px h-5 bg-gray-300" />
        <svg width="10" height="6" viewBox="0 0 10 6" className="text-gray-300 -mt-px">
          <path d="M5 6 L0 0 L10 0Z" fill="currentColor" />
        </svg>
      </div>
    );
  }
  // Branching connector: horizontal line spanning children
  return (
    <div className="flex flex-col items-center my-1">
      <div className="w-px h-5 bg-gray-300" />
      <div className="w-px h-2 bg-gray-300" />
    </div>
  );
}

export default function LineageChart({ bladeId }) {
  const [lineage, setLineage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!bladeId) return;
    setLoading(true);
    bladesApi.lineage(bladeId)
      .then(({ data }) => setLineage(data))
      .catch(() => setLineage(null))
      .finally(() => setLoading(false));
  }, [bladeId]);

  if (loading) return <p className="text-xs text-gray-400 py-4 text-center">Loading…</p>;
  if (!lineage) return null;

  const { node, ancestors, descendants } = lineage;
  const hasConnections = ancestors.length > 0 || descendants.length > 0;

  if (!hasConnections) {
    return (
      <p className="text-xs text-gray-400 py-4 text-center">
        No lineage connections — this design was not copied from or to another design.
      </p>
    );
  }

  return (
    <div className="flex flex-col items-center py-4 select-none">

      {/* Ancestors */}
      {ancestors.map((a, i) => (
        <div key={a.id} className="flex flex-col items-center">
          <NodeCard node={a} />
          <Connector />
        </div>
      ))}

      {/* Current node */}
      <NodeCard node={node} isCurrent />

      {/* Descendants */}
      {descendants.length > 0 && (
        <>
          <Connector branches={descendants.length} />

          {descendants.length === 1 ? (
            <NodeCard node={descendants[0]} />
          ) : (
            <div className="relative flex items-start gap-3 flex-wrap justify-center">
              {/* Horizontal branch bar */}
              <div
                className="absolute top-0 left-1/2 -translate-x-1/2 h-px bg-gray-300"
                style={{ width: `${Math.min(descendants.length, 4) * 180}px` }}
              />
              {descendants.map((d) => (
                <div key={d.id} className="flex flex-col items-center pt-2">
                  <div className="w-px h-3 bg-gray-300 mb-1" />
                  <NodeCard node={d} />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
